import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGuard from "@/components/RoleGuard";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import { useAuth } from "@/lib/auth";
import { isClientRole } from "@/lib/permissions";
import { ProjectsRegister, ProjectDetail } from "@/pages/Projects";
import { ITPRegister, ITPDetail } from "@/pages/ITP";
import ReviewImportedITP from "@/pages/ReviewImportedITP";
import TemplateBuilder from "@/pages/TemplateBuilder";
import { LotRegister, CreateLot } from "@/pages/Lots";
import LotDetail from "@/pages/LotDetail";
import ClientReview from "@/pages/ClientReview";
import EvidenceRegister from "@/pages/EvidenceRegister";
import NCRRegister from "@/pages/NCRRegister";
import AuditTrail from "@/pages/AuditTrail";
import EmailOutbox from "@/pages/EmailOutbox";
import Plans from "@/pages/Plans";
import PlanViewer from "@/pages/PlanViewer";
import LotMaps from "@/pages/LotMaps";
import Users from "@/pages/Users";
import OrganisationSettings from "@/pages/OrganisationSettings";
import PlatformCompanies from "@/pages/platform/Companies";
import PlatformAccessRequests from "@/pages/platform/AccessRequests";
import PlatformSettings from "@/pages/platform/Settings";
import SurveyUpload from "@/pages/SurveyUpload";
import NumberingSeries from "@/pages/NumberingSeries";
import { ProjectContextProvider } from "@/lib/projectContext";
import ActiveITP from "@/pages/ActiveITP";
import PublicSignatureV2 from "@/pages/PublicSignatureV2";
import PublicReleaseV2 from "@/pages/PublicReleaseV2";
import ChangeTemporaryPassword from "@/pages/ChangeTemporaryPassword";
import AccountSettings from "@/pages/AccountSettings";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

/* When user.must_change_password=true, force them through /change-password.
 * Allow only /change-password and /login. Everything else redirects. */
function ProtectedWithPasswordGuard({ children }) {
  const { user, loading } = useAuth();
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (loading) return null;
  if (user && user.must_change_password === true && path !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function Protected({ children }) {
  return (
    <ProtectedWithPasswordGuard>
      <Layout>{children}</Layout>
    </ProtectedWithPasswordGuard>
  );
}

function HomeRoute() {
  const { user } = useAuth();
  if (user && user.role === "platform_admin") return <Navigate to="/platform/companies" replace />;
  if (user && isClientRole(user.role)) return <ClientDashboard />;
  // New workflow: land on the project picker; users pick a project to enter project-scoped mode.
  return <Navigate to="/projects" replace />;
}

function ProjectScoped({ children }) {
  return (
    <Protected>
      <ProjectContextProvider>{children}</ProjectContextProvider>
    </Protected>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="bottom-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangeTemporaryPassword /></ProtectedRoute>} />
          <Route path="/account/settings" element={<Protected><AccountSettings /></Protected>} />
          <Route path="/client-review/:token" element={<ClientReview />} />
          <Route path="/approval-review/:token" element={<ClientReview reviewKind="approval_point" />} />
          <Route path="/survey-upload/:token" element={<SurveyUpload />} />
          <Route path="/projects/:projectId/numbering-series" element={<Protected><RoleGuard allow={["admin","qa_manager","site_engineer"]}><NumberingSeries /></RoleGuard></Protected>} />
          <Route path="/itp-sign/:token" element={<PublicSignatureV2 />} />
          <Route path="/itp-release/:token" element={<PublicReleaseV2 />} />
          <Route path="/lots/:lotId/itp-v2/:activeId" element={<Protected><ActiveITP /></Protected>} />
          <Route path="/" element={<Protected><HomeRoute /></Protected>} />
          <Route path="/platform/companies" element={<Protected><RoleGuard allow={["platform_admin"]}><PlatformCompanies /></RoleGuard></Protected>} />
          <Route path="/platform/access-requests" element={<Protected><RoleGuard allow={["platform_admin"]}><PlatformAccessRequests /></RoleGuard></Protected>} />
          <Route path="/platform/settings" element={<Protected><RoleGuard allow={["platform_admin"]}><PlatformSettings /></RoleGuard></Protected>} />
          <Route path="/projects" element={<Protected><ProjectsRegister /></Protected>} />
          <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
          <Route path="/itp" element={<Protected><ITPRegister /></Protected>} />
          <Route path="/itp/builder/:id" element={<Protected><RoleGuard allow={["admin","qa_manager"]}><TemplateBuilder /></RoleGuard></Protected>} />
          <Route path="/itp/:id/review" element={<Protected><RoleGuard allow={["admin","qa_manager"]}><ReviewImportedITP /></RoleGuard></Protected>} />
          <Route path="/itp/:id" element={<Protected><ITPDetail /></Protected>} />
          <Route path="/lots" element={<Protected><LotRegister /></Protected>} />
          <Route path="/lots/new" element={<Protected><RoleGuard allow={["admin","site_engineer","qa_manager"]}><CreateLot /></RoleGuard></Protected>} />
          <Route path="/lots/:id" element={<Protected><LotDetail /></Protected>} />
          <Route path="/evidence" element={<Protected><EvidenceRegister /></Protected>} />
          <Route path="/plans" element={<Protected><Plans /></Protected>} />
          <Route path="/plans/:id" element={<Protected><PlanViewer /></Protected>} />
          <Route path="/lot-maps" element={<Protected><LotMaps /></Protected>} />
          <Route path="/users" element={<Protected><RoleGuard allow={["admin","qa_manager"]}><Users /></RoleGuard></Protected>} />
          <Route path="/ncrs" element={<Protected><NCRRegister /></Protected>} />
          <Route path="/audit" element={<Protected><AuditTrail /></Protected>} />
          <Route path="/emails" element={<Protected><EmailOutbox /></Protected>} />
          <Route path="/settings/organisation" element={<Protected><RoleGuard allow={["admin","qa_manager","site_engineer","client","subcontractor","external_reviewer","project_engineer"]}><OrganisationSettings /></RoleGuard></Protected>} />
          {/* ---------- Project-scoped routes ------------------------------------ */}
          <Route path="/p/:projectId/dashboard" element={<ProjectScoped><Dashboard /></ProjectScoped>} />
          <Route path="/p/:projectId/itp" element={<ProjectScoped><ITPRegister /></ProjectScoped>} />
          <Route path="/p/:projectId/itp/builder/:id" element={<ProjectScoped><RoleGuard allow={["admin","qa_manager"]}><TemplateBuilder /></RoleGuard></ProjectScoped>} />
          <Route path="/p/:projectId/itp/:id/review" element={<ProjectScoped><RoleGuard allow={["admin","qa_manager"]}><ReviewImportedITP /></RoleGuard></ProjectScoped>} />
          <Route path="/p/:projectId/itp/:id" element={<ProjectScoped><ITPDetail /></ProjectScoped>} />
          <Route path="/p/:projectId/lots" element={<ProjectScoped><LotRegister /></ProjectScoped>} />
          <Route path="/p/:projectId/lots/new" element={<ProjectScoped><RoleGuard allow={["admin","site_engineer","qa_manager"]}><CreateLot /></RoleGuard></ProjectScoped>} />
          <Route path="/p/:projectId/lots/:id" element={<ProjectScoped><LotDetail /></ProjectScoped>} />
          <Route path="/p/:projectId/evidence" element={<ProjectScoped><EvidenceRegister /></ProjectScoped>} />
          <Route path="/p/:projectId/plans" element={<ProjectScoped><Plans /></ProjectScoped>} />
          <Route path="/p/:projectId/plans/:id" element={<ProjectScoped><PlanViewer /></ProjectScoped>} />
          <Route path="/p/:projectId/lot-maps" element={<ProjectScoped><LotMaps /></ProjectScoped>} />
          <Route path="/p/:projectId/ncrs" element={<ProjectScoped><NCRRegister /></ProjectScoped>} />
          <Route path="/p/:projectId/audit" element={<ProjectScoped><AuditTrail /></ProjectScoped>} />
          <Route path="/p/:projectId/emails" element={<ProjectScoped><EmailOutbox /></ProjectScoped>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
